import { cs } from '../../../lib/currency';
import { useState, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Loader2, ChevronRight, Users, Calendar, Trophy, Briefcase, Sun, Coins, Info, MapPin } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import api from '../../../services/api';
import { toast } from 'sonner';
import { toQueryParams } from './DateRangePicker';
import { useFeatures } from '../../../context/FeatureContext';

// iter302: deterministic toFixed(2) — see Transactions.js notes.
const formatMoney = (v) => {
  const n = Number(v);
  const safe = Number.isFinite(n) ? n : 0;
  const [whole, dec] = safe.toFixed(2).split('.');
  const sign = whole.startsWith('-') ? '-' : '';
  const abs = sign ? whole.slice(1) : whole;
  return `${sign}${cs()}${abs.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${dec}`;
};

// iter276: 6-card layout matches the dashboard's revenue categories
// (subscriptions / coaching / tournaments / lane bookings / camps / other).
// The backend `compute_revenue_streams` now reuses the same canonical
// revenue aggregator the dashboard does, so the totals here always
// reconcile with the dashboard Revenue Overview tile + Cash Flow Trend.
// iter335d — each stream card is gated by the tenant plan feature(s) that
// produce that revenue. A card shows when ANY of its featureKeys is
// entitled. `other` (manual invoices) is always shown.
const STREAM_CARDS = [
  { key: 'subscriptions', label: 'Subscriptions', icon: Users, color: 'emerald', hint: 'Monthly program fees & enrollments', featureKeys: ['enrollments.core'] },
  { key: 'coaching', label: 'Coaching', icon: Briefcase, color: 'purple', hint: 'Small group & 1:1 coaching invoices', featureKeys: ['bookings.small_groups', 'bookings.coaching'] },
  { key: 'tournaments', label: 'Tournaments', icon: Trophy, color: 'amber', hint: 'Tournament registrations', featureKeys: ['tournaments.registrations'] },
  { key: 'lane_bookings', label: 'Lane Bookings', icon: Calendar, color: 'blue', hint: 'Lane rental revenue', featureKeys: ['bookings.lanes'] },
  { key: 'camps', label: 'Camps', icon: Sun, color: 'orange', hint: 'Camp & clinic registrations', featureKeys: ['camps.core'] },
  { key: 'other', label: 'Other', icon: Coins, color: 'slate', hint: 'Manual invoices & miscellaneous', featureKeys: null },
];

const COLOR_CLASSES = {
  emerald: { text: 'text-emerald-700', bg: 'bg-emerald-100', border: 'border-emerald-200', hover: 'hover:bg-emerald-50' },
  blue: { text: 'text-blue-700', bg: 'bg-blue-100', border: 'border-blue-200', hover: 'hover:bg-blue-50' },
  amber: { text: 'text-amber-700', bg: 'bg-amber-100', border: 'border-amber-200', hover: 'hover:bg-amber-50' },
  purple: { text: 'text-purple-700', bg: 'bg-purple-100', border: 'border-purple-200', hover: 'hover:bg-purple-50' },
  orange: { text: 'text-orange-700', bg: 'bg-orange-100', border: 'border-orange-200', hover: 'hover:bg-orange-50' },
  slate: { text: 'text-slate-700', bg: 'bg-slate-100', border: 'border-slate-200', hover: 'hover:bg-slate-50' },
};

// Streams that have a dedicated drill-down detail tab. Camps + Other
// only appear on the overview for now — clicking their cards is a no-op
// until per-stream detail pages are built.
const STREAMS_WITH_DETAIL = new Set(['subscriptions', 'coaching', 'tournaments', 'lane_bookings']);

export const BillingOverviewTab = ({ dateRange, locationId, onNavigate }) => {
  const { isPlanFeatureEnabled } = useFeatures();
  const visibleStreamCards = STREAM_CARDS.filter(
    (c) => !c.featureKeys || c.featureKeys.some((k) => isPlanFeatureEnabled(k)),
  );
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [streams, setStreams] = useState({});
  const [periodLabel, setPeriodLabel] = useState('');
  // ARM UI-02 (2026-07) — backend now returns `filter_scope` so we can
  // render a "Location: X" chip and users see which cards are scoped.
  const [filterScope, setFilterScope] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = toQueryParams(dateRange);
      params.append('include_items', 'false');
      if (locationId && locationId !== 'all') params.append('location_id', locationId);
      const { data } = await api.get(`/admin/billing/revenue-streams?${params.toString()}`);
      setStreams(data.streams || {});
      setPeriodLabel(data.period_label || '');
      setFilterScope(data.filter_scope || null);  // ARM UI-02
      setHasLoaded(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, [dateRange, locationId]);

  // iter372 — On-demand ONLY. This revenue aggregation is expensive on large
  // tenants (it was 524-ing on auto-load), so we no longer fetch on mount or
  // on every filter change. The admin sets the date range + location above
  // and presses "Run Overview".

  // Grand totals across all 4 streams
  const grandTotals = Object.values(visibleStreamCards).reduce((acc, c) => {
    const s = streams[c.key] || {};
    acc.total_revenue += Number(s.total_revenue || 0);
    acc.revenue_this_month += Number(s.revenue_this_month || 0);
    acc.outstanding += Number(s.outstanding || 0);
    acc.pending += Number(s.pending || 0);
    acc.outstanding_count += Number(s.outstanding_count || 0);
    acc.pending_count += Number(s.pending_count || 0);
    return acc;
  }, { total_revenue: 0, revenue_this_month: 0, outstanding: 0, pending: 0, outstanding_count: 0, pending_count: 0 });

  if (loading) {
    return (
      <div className="py-16 text-center">
        <Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" />
        <p className="text-sm text-slate-500 mt-2">Running overview…</p>
      </div>
    );
  }

  // iter372 — On-demand empty state. No data is fetched until the admin
  // presses Run, so large tenants never auto-trigger the heavy aggregation.
  if (!hasLoaded) {
    return (
      <Card className="border-dashed" data-testid="billing-overview-ondemand">
        <CardContent className="p-10 text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
            <Coins className="h-6 w-6 text-slate-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Revenue Overview</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              Set the date range and location in the filters above, then run the
              report. We don&apos;t load it automatically because this summary is
              heavy on large accounts.
            </p>
          </div>
          <Button onClick={fetchData} data-testid="billing-overview-run-btn">
            Run Overview
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="billing-overview-tab">
      {/* Hero: all-streams totals */}
      <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0">
        <CardContent className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs uppercase tracking-wider text-slate-300">Revenue · {periodLabel}</p>
            <div className="flex items-center gap-2">
            {/* ARM UI-02 — Location scope chip so users know all cards on
                this tab are filtered by the picked location.  */}
            {filterScope?.is_location_scoped && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-blue-500/20 border border-blue-400/40 text-blue-100 text-xs px-2 py-0.5"
                data-testid="billing-overview-location-scope-chip"
              >
                <MapPin className="h-3 w-3" />
                Location-scoped
              </span>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={fetchData}
              className="h-7 bg-white/10 hover:bg-white/20 text-white border-0"
              data-testid="billing-overview-rerun-btn"
            >
              Re-run
            </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-4">
            <div>
              <p className="text-xs text-slate-400">This Period</p>
              <p className="text-3xl font-bold" data-testid="overview-period-revenue">
                {formatMoney(grandTotals.revenue_this_month)}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">All-Time Revenue</p>
              <p className="text-3xl font-bold">{formatMoney(grandTotals.total_revenue)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 flex items-center gap-1">
                Outstanding ({grandTotals.outstanding_count})
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-full hover:text-red-200 focus:outline-none"
                        data-testid="overview-outstanding-tooltip-trigger"
                        aria-label="What counts as Outstanding?"
                      >
                        <Info className="h-3 w-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-left leading-relaxed bg-slate-800 text-slate-100">
                      Sum of every unpaid invoice (across monthly, tournament, lane, small-group, camp, and manual streams). Any issued invoice that hasn&apos;t been paid is outstanding &mdash; numbers come directly from actual invoice rows, no forward projection.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </p>
              <p className="text-3xl font-bold text-red-300" data-testid="overview-outstanding">
                {formatMoney(grandTotals.outstanding)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 6 stream cards with "View Details" — iter276 alignment with
          dashboard revenue categories. Camps + Other don't have a
          dedicated drill-down tab yet, so their cards skip the "View
          Details" button and aren't clickable. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {visibleStreamCards.map((c) => {
          const s = streams[c.key] || {};
          const cc = COLOR_CLASSES[c.color];
          const Icon = c.icon;
          const hasDetail = STREAMS_WITH_DETAIL.has(c.key);
          return (
            <Card
              key={c.key}
              className={`${cc.border} ${hasDetail ? `${cc.hover} cursor-pointer` : ''} transition`}
              onClick={() => { if (hasDetail && onNavigate) onNavigate(c.key); }}
              data-testid={`overview-card-${c.key}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${cc.bg} flex items-center justify-center`}>
                      <Icon className={`h-5 w-5 ${cc.text}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{c.label}</h3>
                      <p className="text-xs text-slate-500">{c.hint}</p>
                    </div>
                  </div>
                  {hasDetail && (
                    <Button variant="ghost" size="sm" className="text-slate-500">
                      View Details <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase">This Period</p>
                    <p className={`text-base font-bold ${cc.text}`}>
                      {formatMoney(s.revenue_this_month)}
                    </p>
                    {Number(s.refunded_this_period || 0) > 0 && (
                      <p className="text-[10px] text-rose-600 mt-0.5" data-testid={`overview-refunded-period-${c.key}`}>
                        refunded {formatMoney(s.refunded_this_period)}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase">All Time</p>
                    <p className="text-base font-bold text-slate-700">
                      {formatMoney(s.total_revenue)}
                    </p>
                    {Number(s.refunded_all_time || 0) > 0 && (
                      <p className="text-[10px] text-rose-600 mt-0.5" data-testid={`overview-refunded-alltime-${c.key}`}>
                        refunded {formatMoney(s.refunded_all_time)}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] text-red-700 uppercase">
                      Outstanding ({s.outstanding_count || 0})
                    </p>
                    <p className="text-base font-bold text-red-700">{formatMoney(s.outstanding)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Category source map — answers "where is small group / 1:1 captured?". */}
      <Card className="border-dashed border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-slate-500 space-y-1">
              <p className="font-medium text-slate-700">Where revenue lands</p>
              <ul className="space-y-0.5">
                <li><strong className="text-emerald-700">Subscriptions</strong> — all paid enrollments (monthly, quarterly, biannual) + recurring `monthly_invoices`.</li>
                <li><strong className="text-purple-700">Coaching</strong> — small-group sessions &amp; 1:1 coaching invoices (`small_group_invoices`).</li>
                <li><strong className="text-amber-700">Tournaments</strong> — tournament registrations.</li>
                <li><strong className="text-blue-700">Lane Bookings</strong> — lane rentals (paid status only).</li>
                <li><strong className="text-orange-700">Camps</strong> — camp &amp; clinic registrations (`camp_registrations`).</li>
                <li><strong className="text-slate-700">Other</strong> — manual invoices &amp; legacy miscellaneous payments.</li>
              </ul>
              <p className="pt-1 text-slate-400">
                Totals here are sourced from the same aggregator the dashboard uses, so the
                &quot;This Period&quot; sum equals the dashboard&apos;s Revenue Overview for the same window.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
